import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { PageShell } from "@/components/page-shell";
import { ObfuscatedEmail } from "@/components/obfuscated-email";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "WeVang! — Brand & Strategy for Those Done with Generic" },
      {
        name: "description",
        content:
          "For leaders held back by fragmented approaches — WeVang! brings strategy, brand, and growth into one system.",
      },
      { property: "og:title", content: "WeVang! — Brand & Strategy for Those Done with Generic" },
      {
        property: "og:description",
        content:
          "For leaders held back by fragmented approaches — WeVang! brings strategy, brand, and growth into one system.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://www.wevang.com/" },
    ],
    links: [{ rel: "canonical", href: "https://www.wevang.com/" }],
  }),
  component: Index,
});

function Eyebrow({
  children,
  align = "left",
}: {
  children: ReactNode;
  align?: "left" | "right";
}) {
  const isRight = align === "right";
  const line = <span className="mt-[0.55rem] h-px w-10 shrink-0 bg-primary" />;
  const text = (
    <p className="text-[14px] font-bold uppercase leading-relaxed tracking-[0.28em] text-primary">
      {children}
    </p>
  );
  return (
    <div className={`flex items-start gap-4 ${isRight ? "justify-end" : ""}`}>
      {isRight ? text : line}
      {isRight ? line : text}
    </div>
  );
}

function Section({
  children,
  watermark,
  align = "left",
  className = "",
}: {
  children: ReactNode;
  watermark?: string;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <section
      className={`relative isolate overflow-hidden px-6 py-24 md:px-12 md:py-36 ${className}`}
    >
      {watermark && (
        <span
          aria-hidden
          className={`watermark pointer-events-none absolute bottom-4 -z-10 hidden select-none text-[6.5vw] opacity-[0.07] lg:block ${
            align === "right" ? "left-2" : "right-2"
          }`}
        >
          {watermark}
        </span>
      )}
      <div
        className={`relative mx-auto max-w-[1400px] ${align === "right" ? "flex justify-end" : ""}`}
      >
        <div className="max-w-4xl">{children}</div>
      </div>
    </section>
  );
}

function Index() {
  return (
    <PageShell overHero>
      {/* SECTION 1 — HERO */}
      <section className="relative isolate overflow-hidden">
        <div
          aria-hidden
          className="hero-gradient absolute inset-0 -z-10"
        />
        <div className="mx-auto w-full max-w-[1400px] px-6 pb-40 pt-48 md:px-12 md:pb-56 md:pt-60">
          <h1 className="max-w-5xl font-display text-[13vw] font-extrabold leading-[0.92] tracking-tighter text-white sm:text-[72px] lg:text-[88px]">
            We don&apos;t just participate in the market.<br />
            <span className="box-decoration-clone inline-block bg-primary px-3 py-1 mt-3 text-white">
              We redefine it.
            </span>

          </h1>
          <p className="mt-10 max-w-2xl font-sans text-[22px] font-light leading-[1.6] text-white/90 md:text-[26px]">
            WeVang! exists to empower the relentless — building industry-defining brands from scratch
            and unlocking their momentum across every facet of their market.
          </p>
        </div>
      </section>

      {/* SECTION 2 — PROBLEM > SOLUTION */}
      <Section watermark="Enough">
        <Eyebrow>THE ERA OF &quot;GOOD ENOUGH&quot; IS OVER</Eyebrow>
        <h2 className="mt-10 font-display text-[40px] font-extrabold leading-[1.05] tracking-tighter text-foreground md:text-[64px]">
          Born from the frustration of diluted strategies and stalled traction, WeVang! emerged to
          eliminate the chaos.
        </h2>
        <p className="mt-8 font-sans text-[20px] font-normal leading-[1.6] text-foreground/60 md:text-[22px]">
          We saw visionaries held back by fragmented approaches and decided to{" "}
          <span className="font-semibold text-primary">forge a new path</span>: one where excellence
          is non-negotiable and every product, message, and design delivers{" "}
          <span className="font-semibold text-primary">
            undeniable purpose, power, and precision
          </span>
          .
        </p>
      </Section>

      {/* SECTION 3 — PHILOSOPHY */}
      <div className="surface-light">
        <Section watermark="Conviction" align="right">
          <div className="text-right">
            <Eyebrow align="right">UNAPOLOGETIC EXCELLENCE</Eyebrow>
            <h2 className="mt-10 font-display text-[36px] font-extrabold leading-[1.05] tracking-tighter text-foreground md:text-[56px]">
              Our ethos is simple:&nbsp;<span className="text-primary">We lead.</span><br />
              <span className="text-primary">We dominate. No apologies.</span>
            </h2>
            <p className="mt-8 font-sans text-[20px] font-normal leading-[1.6] text-foreground/60">
              This isn&apos;t just a motto; it&apos;s the operational principle embedded in every
              strategy, every design, every growth initiative. We believe brave ideas should
              dominate, iconic brands should ignite, and excellence should be the only standard.
            </p>
          </div>
        </Section>
      </div>

      {/* SECTION 4 — HOW WE WORK */}
      <Section watermark="Systems">
        <Eyebrow>THE BIG PICTURE</Eyebrow>
        <h2 className="mt-10 font-display text-[36px] font-extrabold leading-[1.05] tracking-tighter text-foreground md:text-[56px]">
          Specialists see parts. <br /><span className="text-primary">We see systems.</span>
        </h2>
        <p className="mt-8 font-sans text-[20px] font-normal leading-[1.6] text-foreground/60">
          Strategy, product, market, organization, design — they&apos;re not separate disciplines.
          Our value isn&apos;t in knowing one deeper than anyone else. It&apos;s in{" "}
          <span className="font-semibold text-primary">exercising judgment under uncertainty</span>{" "}
          and helping your company move with stronger internal coherence.
        </p>
      </Section>

      {/* SECTION 5 — WHAT WE DO */}
      <section className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-36">
        <div className="rule-red" />
        <div className="grid gap-16 pt-16 md:grid-cols-2 md:gap-20">
          <Offer
            heading="Alliances"
            subhead="Embedded leadership."
            body={
              <>
                We provide embedded strategic command —{" "}
                <span className="font-semibold text-primary">a seat at your executive table</span>,
                inside the room, not outside advising it. We are the unseen force at your strategic
                core, driving decisive market advantage and securing your enduring supremacy.
              </>
            }
            to="/alliances"
            cta="Explore Alliances"
          />
          <Offer
            heading="Journeys"
            subhead="A system to scale to your ambition."
            body={
              <>
                A proven system that takes your vision from brilliant idea to market leadership. We
                guide you through every critical inflection — from product-market fit and strategic
                positioning, to{" "}
                <span className="font-semibold text-primary">a brand that owns its category</span> —
                ensuring that at every stage, you&apos;re dominating your next challenge.
              </>
            }
            to="/journeys"
            cta="Explore Journeys"
          />
        </div>
      </section>

      {/* SECTION 6 — THE TEAM */}
      <div className="surface-light">
        <Section watermark="Collective" align="right">
          <div className="text-right">
            <Eyebrow align="right">A COLLECTIVE WORKING AS A SYSTEM</Eyebrow>
            <div className="mt-10 flex flex-col items-end gap-5 text-right">
              <p className="font-display text-[48px] font-extrabold leading-[1.1] tracking-tight text-foreground">
                We are...
              </p>
              <p className="font-display text-[40px] font-extrabold leading-[1.1] tracking-tight text-foreground">
                executive leaders and strategists.
              </p>
              <p className="font-display text-[40px] font-bold leading-[1.1] tracking-tight text-foreground">
                Lawyers and engineers.
              </p>
              <p className="font-display text-[34px] font-semibold leading-[1.15] tracking-tight text-foreground">
                Artists, photographers, and filmmakers.
              </p>
              <p className="font-sans text-[28px] font-normal leading-[1.4] text-foreground/70">
                Graphic designers, digital marketers, musicians, writers, and AI specialists.
              </p>
            </div>
            <Link
              to="/about"
              className="mt-12 inline-flex items-center gap-3 border-b border-primary pb-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary"
            >
              Meet the team <ArrowRight size={14} />
            </Link>
          </div>
        </Section>
      </div>

      {/* SECTION 7 + 8 — WHY WEVANG / CTA */}
      <section className="relative isolate overflow-hidden px-6 py-28 md:px-12 md:py-44">
        <span
          aria-hidden
          className="watermark pointer-events-none absolute left-1/2 top-1/2 -z-10 -translate-x-1/2 -translate-y-1/2 select-none text-[26vw] opacity-[0.07]"
        >
          WeVang
        </span>
        <p className="relative mx-auto max-w-5xl text-center font-display text-[28px] font-bold leading-[1.15] tracking-tight text-foreground md:text-[44px]">
          We are the{" "}
          <span className="text-primary">unseen force at your strategic core</span>, driving decisive
          market advantage and engineering your next era of dominance.
        </p>
      </section>

      <section className="px-6 pb-32 text-center md:px-12 md:pb-44">
        <ObfuscatedEmail className="text-sm tracking-[0.3em] text-foreground/70 transition-colors hover:text-primary" />
      </section>
    </PageShell>
  );
}

function Offer({
  heading,
  subhead,
  body,
  to,
  cta,
}: {
  heading: string;
  subhead: string;
  body: ReactNode;
  to: "/alliances" | "/journeys";
  cta: string;
}) {
  return (
    <div className="flex h-full flex-col border-t border-border pt-8">
      <h2 className="font-display text-[44px] font-extrabold uppercase leading-[1] tracking-tighter text-foreground md:text-[56px]">
        {heading}
      </h2>
      <p className="mt-6 font-display text-[20px] font-bold uppercase tracking-tight text-primary">
        {subhead}
      </p>
      <div className="flex-1">
        <p className="mt-6 font-sans text-[18px] font-normal leading-[1.7] text-foreground/60 md:text-[20px]">
          {body}
        </p>
      </div>
      <Link
        to={to}
        className="mt-auto inline-flex items-center gap-3 border border-foreground/30 px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-foreground transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground"
      >
        {cta} <ArrowRight size={14} />
      </Link>
    </div>
  );
}
